import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { COUNTRIES } from "@/lib/countries";
import { useGeo } from "@/hooks/use-geo";
import { useUtm } from "@/hooks/use-utm";
import { createSubscription } from "@/lib/beehiiv";
import { trackMarketingLead } from "@/lib/marketing-analytics";
import { sendMailmodoEvent } from "@/lib/mailmodo";
import { sendWebengageCTAEvents } from "@/lib/webengage";
import { FORM_CONFIGS, type FormConfig, type KnowCoding } from "@/config/forms";

/** Default — preserves homepage form IDs (18899/18900/18707/18708). */
const DEFAULT_MODAL_FORM_CONFIG: FormConfig = FORM_CONFIGS.default;

const WHATSAPP_REDIRECT_AFTER_PAINT_MS = 400;

export const EXPERIENCE_OPTIONS = [
  "Student",
  "Working Professional",
  "Self Employed",
  "Unemployed",
  "Retired",
  "Others",
] as const;

export const YEAR_OF_EXPERIENCE_OPTIONS = [
  "0 years (Fresher)",
  "1-2 years",
  "3-5 years",
  "5-10 years",
  "10-15 years",
  "15+ years",
] as const;

interface QuestionOption {
  optionId: number;
  title: string;
}

interface Question {
  id: number;
  formsQuestionIdentityId: number;
  title: string;
  subText: string | null;
  placeholderText: string | null;
  questionCategory: string;
  isRequired: boolean;
  allowOthersOption: boolean | null;
  allowConditionality: boolean | null;
  minimumCharactersAllowed: string | null;
  maximumCharactersAllowed: string | null;
  label: string;
  questionType: string;
  seqNumber: number;
  options: QuestionOption[];
}

interface StandardQuestionFlow {
  optionId: number;
  followUpQuestions: Question[];
}

interface QuestionFlowResponse {
  scholarshipQuestions: Question[];
  exitQuestions: Question[];
  defaultStandardQuestion: Question;
  unconditionalQuestions: Question[];
  standardQuestionsFlow: StandardQuestionFlow[];
  is_whatsapp_disclaimer: boolean;
}

export type HomepageLeadModalProps = {
  open: boolean;
  initialEmail: string;
  onClose: () => void;
  /**
   * Form config that decides which downstream form ID the submission targets.
   * Defaults to the homepage IDs (18899/18900/18707/18708).
   * /resources and /all-resources pass `FORM_CONFIGS.resources` to route to
   * the 20318/20319/20320/20321 form IDs.
   */
  formConfig?: FormConfig;
  /**
   * When true, the know-coding question becomes its own full step with card UI
   * (used by /yt-v2 only). Adds a 4th step: 0=contact, 1=learning path cards,
   * 2=experience+YOE, 3=opt-ins.
   */
  codingPathStep?: boolean;
};

function findQuestionByLabel(
  questionFlow: QuestionFlowResponse | null,
  label: string,
): Question | null {
  if (!questionFlow) return null;
  if (questionFlow.defaultStandardQuestion?.label === label) {
    return questionFlow.defaultStandardQuestion;
  }
  const unconditional = questionFlow.unconditionalQuestions?.find(
    (q) => q.label === label,
  );
  if (unconditional) return unconditional;
  for (const flow of questionFlow.standardQuestionsFlow || []) {
    const found = flow.followUpQuestions?.find((q) => q.label === label);
    if (found) return found;
  }
  return null;
}

function findOptionByTitle(
  question: Question | null,
  title: string,
): QuestionOption | null {
  if (!question?.options) return null;
  return (
    question.options.find(
      (opt) => opt.title.toLowerCase() === title.toLowerCase(),
    ) || null
  );
}

export function useHomepageLeadModal({
  open,
  initialEmail,
  onClose,
  formConfig = DEFAULT_MODAL_FORM_CONFIG,
  codingPathStep = false,
}: HomepageLeadModalProps) {
  const lastStepIndex = codingPathStep ? 3 : 2;
  const geo = useGeo();
  const utm = useUtm();

  const [step, setStep] = useState(0);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [dialCode, setDialCode] = useState("+91");
  const [dialCountryCode, setDialCountryCode] = useState("IN");
  const [phone, setPhone] = useState("");
  const [experience, setExperience] = useState("");
  const [experienceOptionId, setExperienceOptionId] = useState<number | null>(
    null,
  );
  const [yearOfExperience, setYearOfExperience] = useState("");
  const [knowCoding, setKnowCoding] = useState<KnowCoding>("");
  const [enrollMastermind, setEnrollMastermind] = useState(true);
  const [subscribeNewsletter, setSubscribeNewsletter] = useState(true);
  const [country, setCountry] = useState("");
  const [countryCode, setCountryCode] = useState("");
  const [countryEditable, setCountryEditable] = useState(false);

  const [experienceOpen, setExperienceOpen] = useState(false);
  const [yearOfExperienceOpen, setYearOfExperienceOpen] = useState(false);
  const [dialCodeOpen, setDialCodeOpen] = useState(false);

  const [leadUUID, setLeadUUID] = useState<string | null>(null);
  const step1SentRef = useRef(false);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [successData, setSuccessData] = useState<{
    redirectUrl: string;
  } | null>(null);

  const [questionFlow, setQuestionFlow] = useState<QuestionFlowResponse | null>(
    null,
  );

  const professionalExperienceOptions = useMemo((): QuestionOption[] | null => {
    const opts = questionFlow?.defaultStandardQuestion?.options;
    return opts?.length ? opts : null;
  }, [questionFlow]);

  const selectedCountry = COUNTRIES.find((c) => c.code === dialCountryCode);
  const phoneMin = selectedCountry
    ? Array.isArray(selectedCountry.phoneLength)
      ? selectedCountry.phoneLength[0]
      : selectedCountry.phoneLength
    : 7;
  const phoneMax = selectedCountry
    ? Array.isArray(selectedCountry.phoneLength)
      ? selectedCountry.phoneLength[1]
      : selectedCountry.phoneLength
    : 15;

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setName("");
    setEmail(initialEmail.trim().toLowerCase());
    setPhone("");
    setExperience("");
    setExperienceOptionId(null);
    setYearOfExperience("");
    setKnowCoding("");
    setEnrollMastermind(true);
    setSubscribeNewsletter(true);
    setError("");
    setFieldErrors({});
    setSuccessData(null);
    setLoading(false);
    setExperienceOpen(false);
    setYearOfExperienceOpen(false);
    setDialCodeOpen(false);
    setLeadUUID(null);
    step1SentRef.current = false;
  }, [open, initialEmail]);

  // Fire Step 1 as soon as modal opens and geo is loaded — captures email early
  useEffect(() => {
    if (!open || !geo.loaded || step1SentRef.current) return;
    const resolvedEmail = initialEmail.trim().toLowerCase();
    if (!resolvedEmail) return;
    step1SentRef.current = true;
    const resolvedCountryCode = geo.error ? "IN" : geo.countryCode;
    const formId = formConfig.pickFormId({ knowCoding: "", countryCode: resolvedCountryCode });
    sendMailmodoEvent(resolvedEmail, "Application Form Viewed", { form_id: formId, email: resolvedEmail });
    const apiUrl = import.meta.env.VITE_FORM_API_URL as string;
    fetch(`${apiUrl}/api-form-persistent`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${import.meta.env.VITE_FORMS_API_TOKEN as string}`,
      },
      body: JSON.stringify({
        email: resolvedEmail,
        formId,
        utm_source: utm.utmSource || undefined,
        utm_medium: utm.utmMedium || undefined,
        utm_campaign: utm.utmCampaign || undefined,
        utm_content: utm.utmContent || undefined,
        utm_term: utm.utmTerm || undefined,
        utm_adset: utm.utmAdset || undefined,
      }),
    })
      .then((r) => r.json())
      .then((data: { status: string; leadUUID?: string }) => {
        if (data.status === "success" && data.leadUUID) {
          setLeadUUID(data.leadUUID);
          localStorage.setItem("leadUUID", data.leadUUID);
        }
      })
      .catch(() => {});
  }, [open, geo.loaded, geo.error, geo.countryCode, initialEmail, formConfig, utm]);

  useEffect(() => {
    async function fetchQuestions() {
      try {
        const formId = formConfig.pickFormId({ knowCoding, countryCode });
        const apiUrl = import.meta.env.VITE_FORM_API_URL;
        const response = await fetch(`${apiUrl}/forms/${formId}/questionflow`);
        const data: QuestionFlowResponse | null = response.ok
          ? await response.json()
          : null;
        if (data) setQuestionFlow(data);
        else console.warn("⚠️ Failed to fetch question flow");
      } catch (e) {
        console.error("❌ Error fetching questions:", e);
      }
    }
    if (open && countryCode) fetchQuestions();
  }, [countryCode, knowCoding, open]);

  useEffect(() => {
    if (!geo.loaded) return;
    if (geo.error) {
      setCountryEditable(true);
      setCountry("India");
      setCountryCode("IN");
      return;
    }
    setCountry(geo.country);
    setCountryCode(geo.countryCode);
    if (geo.dialCode) {
      setDialCode(geo.dialCode);
      setDialCountryCode(geo.countryCode);
    }
  }, [geo.loaded, geo.error, geo.country, geo.countryCode, geo.dialCode]);

  useEffect(() => {
    const opts = questionFlow?.defaultStandardQuestion?.options;
    if (!opts?.length) return;
    if (!experience) {
      setExperienceOptionId(null);
      return;
    }
    const opt = opts.find(
      (o) => o.title.toLowerCase() === experience.toLowerCase(),
    );
    if (!opt) {
      setExperience("");
      setExperienceOptionId(null);
      setYearOfExperience("");
      return;
    }
    setExperienceOptionId(opt.optionId);
  }, [questionFlow, experience]);

  useEffect(() => {
    if (!successData) return;
    const timer = setTimeout(() => {
      window.location.href = successData.redirectUrl;
    }, WHATSAPP_REDIRECT_AFTER_PAINT_MS);
    return () => clearTimeout(timer);
  }, [successData]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  const handleCountryChange = useCallback((code: string) => {
    const match = COUNTRIES.find((c) => c.code === code);
    if (match) {
      setCountry(match.name);
      setCountryCode(match.code);
      setDialCode(match.dialCode);
      setDialCountryCode(match.code);
      const max = Array.isArray(match.phoneLength)
        ? match.phoneLength[1]
        : match.phoneLength;
      setPhone((p) => (p.length > max ? p.slice(0, max) : p));
    }
  }, []);

  const validateFullForm = useCallback((): boolean => {
    const errors: Record<string, string> = {};
    if (!name.trim()) {
      errors.name = "Name is required.";
    } else if (name.trim().length < 2) {
      errors.name = "Name must be at least 2 characters.";
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email.trim()) {
      errors.email = "Email is required.";
    } else if (!emailRegex.test(email.trim())) {
      errors.email = "Please enter a valid email address.";
    }
    if (!phone.trim()) {
      errors.phone = "Phone number is required.";
    } else if (phone.length < phoneMin || phone.length > phoneMax) {
      errors.phone =
        phoneMin === phoneMax
          ? `Phone number must be ${phoneMin} digits.`
          : `Phone number must be ${phoneMin}–${phoneMax} digits.`;
    }
    if (!experience) {
      errors.experience = "Please select what describes you the best.";
    }
    if (experience !== "Student" && !yearOfExperience) {
      errors.yearOfExperience = "Please select years of experience.";
    }
    if (knowCoding !== "yes" && knowCoding !== "no") {
      errors.knowCoding = "Please select whether you know coding.";
    }
    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  }, [
    name,
    email,
    phone,
    phoneMin,
    phoneMax,
    experience,
    yearOfExperience,
    knowCoding,
  ]);

  const validateStep = useCallback(
    (s: number): boolean => {
      const errors: Record<string, string> = {};
      if (s === 0) {
        if (!name.trim()) errors.name = "Name is required.";
        else if (name.trim().length < 2)
          errors.name = "Name must be at least 2 characters.";
        if (!geo.loaded) return false;
        if (!phone.trim()) errors.phone = "Phone number is required.";
        else if (phone.length < phoneMin || phone.length > phoneMax) {
          errors.phone =
            phoneMin === phoneMax
              ? `Phone number must be ${phoneMin} digits.`
              : `Phone number must be ${phoneMin}–${phoneMax} digits.`;
        }
      } else if (codingPathStep && s === 1) {
        if (knowCoding !== "yes" && knowCoding !== "no") {
          errors.knowCoding = "Please select a learning path.";
        }
      } else if (codingPathStep && s === 2) {
        if (!experience) {
          errors.experience = "Please select what describes you the best.";
        }
        if (experience !== "Student" && !yearOfExperience) {
          errors.yearOfExperience = "Please select years of experience.";
        }
      } else if (!codingPathStep && s === 1) {
        if (knowCoding !== "yes" && knowCoding !== "no") {
          errors.knowCoding = "Please select whether you know coding.";
        }
        if (!experience) {
          errors.experience = "Please select what describes you the best.";
        }
        if (experience !== "Student" && !yearOfExperience) {
          errors.yearOfExperience = "Please select years of experience.";
        }
      }
      setFieldErrors(errors);
      return Object.keys(errors).length === 0;
    },
    [
      name,
      phone,
      phoneMin,
      phoneMax,
      geo.loaded,
      codingPathStep,
      knowCoding,
      experience,
      yearOfExperience,
    ],
  );

  const selectLearningPath = useCallback(
    (val: "yes" | "no") => {
      setKnowCoding(val);
      setStep((s) => Math.min(s + 1, lastStepIndex));
    },
    [lastStepIndex],
  );

  const goNext = useCallback(() => {
    setError("");
    if (!validateStep(step)) return;
    if (step === 1) {
      const formId = formConfig.pickFormId({ knowCoding, countryCode });
      sendMailmodoEvent(email.trim().toLowerCase(), "Application Form Started", {
        form_id: formId,
        email: email.trim().toLowerCase(),
        name: name.trim(),
        phone: `${dialCode}${phone.trim()}`,
      });
    }
    setStep((s) => Math.min(s + 1, lastStepIndex));
  }, [step, validateStep, lastStepIndex, formConfig, countryCode, email, name, dialCode, phone]);

  const goBack = useCallback(() => {
    setError("");
    setFieldErrors({});
    setStep((s) => Math.max(s - 1, 0));
  }, []);

  const displayTotalSteps = codingPathStep ? 4 : 3;

  const displayStepNumber = step + 1;

  const isCurrentStepValid = useMemo(() => {
    if (codingPathStep) {
      switch (step) {
        case 0:
          return (
            geo.loaded &&
            name.trim().length >= 2 &&
            phone.length >= phoneMin &&
            phone.length <= phoneMax
          );
        case 1:
          return knowCoding === "yes" || knowCoding === "no";
        case 2:
          return (
            experience !== "" &&
            (experience === "Student" || yearOfExperience !== "")
          );
        case 3:
          return true;
        default:
          return false;
      }
    }
    switch (step) {
      case 0:
        return (
          geo.loaded &&
          name.trim().length >= 2 &&
          phone.length >= phoneMin &&
          phone.length <= phoneMax
        );
      case 1:
        return (
          (knowCoding === "yes" || knowCoding === "no") &&
          experience !== "" &&
          (experience === "Student" || yearOfExperience !== "")
        );
      case 2:
        return true;
      default:
        return false;
    }
  }, [
    step,
    codingPathStep,
    geo.loaded,
    name,
    phone,
    phoneMin,
    phoneMax,
    knowCoding,
    experience,
    yearOfExperience,
  ]);

  const handleFinalSubmit = useCallback(
    async (e?: React.FormEvent) => {
      e?.preventDefault();
      setError("");
      if (step !== lastStepIndex) return;
      if (!validateFullForm()) return;
      (document.activeElement as HTMLElement | null)?.blur();
      trackMarketingLead();
      setLoading(true);
      try {
        const formId = formConfig.pickFormId({ knowCoding, countryCode });

        let channel = "youtube";
        if (utm.utmSource) {
          const source = utm.utmSource.toLowerCase();
          if (source.includes("instagram") || source === "ig")
            channel = "instagram";
          else if (source.includes("x") || source.includes("twitter"))
            channel = "x";
          else if (source.includes("youtube") || source === "yt")
            channel = "youtube";
        }

        const questionResponses: Array<Record<string, unknown>> = [];
        let experienceLabelForPayload = experience;

        if (questionFlow) {
          const expQuestion =
            findQuestionByLabel(questionFlow, "professional_label") ||
            findQuestionByLabel(questionFlow, "work_experience");
          if (expQuestion && experience) {
            const expOptionId =
              experienceOptionId ??
              findOptionByTitle(expQuestion, experience)?.optionId;
            const expOption =
              expOptionId != null
                ? expQuestion.options?.find((o) => o.optionId === expOptionId) ??
                  findOptionByTitle(expQuestion, experience)
                : null;
            if (expOption) {
              experienceLabelForPayload = expOption.title;
              const isDefaultStandard =
                expQuestion.questionType === "DEFAULT_STANDARD_QUESTION";
              questionResponses.push({
                responses: [expOption.title],
                questionId: expQuestion.id,
                formsQuestionIdentityId: expQuestion.formsQuestionIdentityId,
                questionType: expQuestion.questionType,
                questionCategory: expQuestion.questionCategory,
                ...(isDefaultStandard ? {} : { optionIds: [expOption.optionId] }),
              });
            }
          }

          const yearExpQuestion = findQuestionByLabel(
            questionFlow,
            "work_experience_range",
          );
          if (yearExpQuestion && yearOfExperience) {
            const yearExpOption = findOptionByTitle(
              yearExpQuestion,
              yearOfExperience,
            );
            if (yearExpOption) {
              questionResponses.push({
                optionIds: [yearExpOption.optionId],
                responses: [yearExpOption.title],
                questionId: yearExpQuestion.id,
                formsQuestionIdentityId: yearExpQuestion.formsQuestionIdentityId,
                questionType: yearExpQuestion.questionType,
                questionCategory: yearExpQuestion.questionCategory,
              });
            }
          }
        }

        const apiUrl = import.meta.env.VITE_FORM_API_URL as string;
        const token = import.meta.env.VITE_FORMS_API_TOKEN as string;
        await sendWebengageCTAEvents({
          form_id: formId,
          email: email.trim().toLowerCase(),
          name: name.trim(),
          phone: `${dialCode}${phone.trim()}`,
        });

        // Ensure we have a leadUUID — fire Step 1 inline if it hasn't completed yet
        let uuid = leadUUID ?? localStorage.getItem("leadUUID");
        if (!uuid) {
          const step1Res = await fetch(`${apiUrl}/api-form-persistent`, {
            method: "POST",
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
            body: JSON.stringify({ email: email.trim().toLowerCase(), formId }),
          });
          const step1Data = await step1Res.json() as { status: string; leadUUID?: string };
          if (step1Data.status === "success" && step1Data.leadUUID) {
            uuid = step1Data.leadUUID;
            setLeadUUID(uuid);
            localStorage.setItem("leadUUID", uuid);
          }
        }

        const payload = {
          leadUUID: uuid,
          formId,
          name: name.trim(),
          phone: `${dialCode}${phone.trim()}`,
          channel,
          country,
          countryCode,
          dialCode,
          questions: {
            experience: experienceLabelForPayload,
            yearOfExperience,
          },
          subscribeNewsletter,
          enrollMastermind,
          utm_source: utm.utmSource || undefined,
          utm_medium: utm.utmMedium || undefined,
          utm_campaign: utm.utmCampaign || undefined,
          utm_content: utm.utmContent || undefined,
          utm_term: utm.utmTerm || undefined,
          utm_adset: utm.utmAdset || undefined,
          ...(questionResponses.length > 0 && { questionResponses }),
        };

        const response = await fetch(`${apiUrl}/api-form-persistent`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        const data = await response.json();

        if (!response.ok || data.status === "error") {
          setError(data.message || "Something went wrong. Please try again.");
          return;
        }
        await sendMailmodoEvent(email.trim().toLowerCase(), "Application Form Completed", {
          form_id: formId,
          email: email.trim().toLowerCase(),
          name: name.trim(),
          phone: `${dialCode}${phone.trim()}`,
        });

        setSuccessData({
          redirectUrl: data.redirect_url || "#",
        });

        if (subscribeNewsletter) {
          createSubscription({
            email: email.trim().toLowerCase(),
            send_welcome_email: false,
            reactivate_existing: false,
            utm_source: utm.utmSource || undefined,
            utm_medium: utm.utmMedium || undefined,
            utm_campaign: utm.utmCampaign || undefined,
            utm_term: utm.utmTerm || undefined,
            utm_content: utm.utmContent || undefined,
            referring_site: "https://stayingahead.club",
            custom_fields: name.trim()
              ? [{ name: "Name", value: name.trim() }]
              : undefined,
          }).catch((err) => console.warn("Beehiiv subscription:", err));
        }
      } catch (err) {
        console.error("Submission error:", err);
        setError("Something went wrong. Please try again.");
      } finally {
        setLoading(false);
      }
    },
    [
      validateFullForm,
      knowCoding,
      countryCode,
      utm,
      questionFlow,
      experience,
      experienceOptionId,
      yearOfExperience,
      name,
      email,
      dialCode,
      phone,
      country,
      enrollMastermind,
      subscribeNewsletter,
      step,
      leadUUID,
    ],
  );

  return {
    step,
    displayStepNumber,
    displayTotalSteps,
    name,
    setName,
    email,
    dialCode,
    setDialCode,
    dialCountryCode,
    setDialCountryCode,
    phone,
    setPhone,
    experience,
    setExperience,
    experienceOptionId,
    setExperienceOptionId,
    yearOfExperience,
    setYearOfExperience,
    knowCoding,
    setKnowCoding,
    enrollMastermind,
    setEnrollMastermind,
    subscribeNewsletter,
    setSubscribeNewsletter,
    country,
    countryCode,
    countryEditable,
    setCountryEditable,
    experienceOpen,
    setExperienceOpen,
    yearOfExperienceOpen,
    setYearOfExperienceOpen,
    dialCodeOpen,
    setDialCodeOpen,
    loading,
    error,
    fieldErrors,
    successData,
    professionalExperienceOptions,
    phoneMin,
    phoneMax,
    geo,
    handleCountryChange,
    goNext,
    goBack,
    handleFinalSubmit,
    isCurrentStepValid,
    onClose,
    codingPathStep,
    selectLearningPath,
  };
}
